import { useState, useCallback, useEffect, useRef } from 'react'
import { UseCaseItem, UseCaseSearchParams } from '../types/usecase'
import { useTableOperations } from '@/custom-hooks/useTableOperations'
import {
  useGetCaseIdList,
  useGetCaseList,
  useGetAttackTypeList,
  useGetCaseTagList,
} from '../../utils'

// 表格操作配置
const tableOperationsConfig = {
  filterFieldMap: {
    riskLevel: 'riskLevel',
    resourceLevel: 'resourceLevel',
    attackTypeId: 'attackTypeId',
  },
  sortFieldMap: {
    updateTime: 'ResourceSortFieldUpdateTime',
  },
  enableSorting: true,
  enableFiltering: true,
  enablePagination: true,
}

export const useUseCaseData = (
  taskParams: any = {},
  onUseCaseSelectionChange?: (
    selectedUseCases: string[],
    useCaseTemplateMap: Record<string, string[]>,
    isSelectAll?: boolean,
  ) => void,
) => {
  const [total, setTotal] = useState(0)
  // 搜索参数状态
  const [searchParams, setSearchParams] = useState<UseCaseSearchParams>({
    page: 1,
    pageSize: 10,
    queryType: 'query_bind',
    os: taskParams.os || '',
    templateId: taskParams.selectedTemplates || [],
    filterParams: {
      sceneId: [11],
      verificationModeId: taskParams.verificationModeIds || [],
    },
  })
  const [attackTypes, setAttackTypes] = useState<{ id: string; name: string }[]>([])
  const [caseTagList, setCaseTagList] = useState<any[]>([])

  // 选中的用例
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([])

  // 用例数据状态
  const [useCases, setUseCases] = useState<UseCaseItem[]>([])
  // 全局用例模板映射 - 保存所有已加载过的用例的模板关联信息
  const [globalUseCaseTemplateMap, setGlobalUseCaseTemplateMap] = useState<
    Record<string, string[]>
  >({})
  // 全选操作标志，用于避免重复调用回调
  const isSelectAllOperation = useRef(false)

  // 获取用例列表
  const [getCaseList, loading] = useGetCaseList()
  // 获取用例id列表
  const [getCaseIdList, caseIdList] = useGetCaseIdList()
  // 获取攻击类型列表
  const [getAttackTypeList] = useGetAttackTypeList()
  // 获取用例标签列表
  const [getCaseTagList] = useGetCaseTagList()

  // 防抖定时器
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // 使用表格操作hook
  const { handleTableChange } = useTableOperations({
    searchParams,
    setSearchParams,
    selectedRowKeys: selectedUseCases,
    setSelectedRowKeys: (keys: React.Key[]) => setSelectedUseCases(keys as string[]),
    config: tableOperationsConfig,
  })

  // 重写表格选择逻辑，处理分页情况下的多页选择
  const handleTableSelectChange = useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      // 获取当前页的所有用例ID
      const currentPageUseCaseIds = useCases.map(useCase => useCase.resourceId)

      // 保留其他页的选中状态，只更新当前页的状态
      const otherPagesSelected = selectedUseCases.filter(id => !currentPageUseCaseIds.includes(id))

      // 合并其他页和当前页的选中状态
      const updatedSelectedUseCases = [
        ...otherPagesSelected,
        ...newSelectedRowKeys
          .filter(id => typeof id === 'string' && currentPageUseCaseIds.includes(id))
          .map(id => id as string),
      ]

      setSelectedUseCases(updatedSelectedUseCases)
    },
    [useCases, selectedUseCases],
  )

  // 获取用例数据
  const fetchData = async () => {
    try {
      const res = await getCaseList(searchParams)
      const resp = await getCaseIdList({ ...searchParams, page: undefined, pageSize: undefined })

      if (!res || !res.data || !resp || !resp.data) return
      const attackTypeRes = await getAttackTypeList({
        queryType: 'query_type_create_task_resource_list',
        'createTaskResourceListQuery.templateIds': searchParams.templateId,
      })
      if (attackTypeRes?.data?.attackTypeFilters) {
        setAttackTypes(attackTypeRes.data.attackTypeFilters)
      }
      const caseTagRes = await getCaseTagList({
        ...searchParams,
        page: undefined,
        pageSize: undefined,
      })
      if (caseTagRes?.data?.categoryList) {
        setCaseTagList(caseTagRes.data.categoryList)
      }

      const newUseCases = res.data.list
      setUseCases(newUseCases)
      setTotal(res.data.total)

      // 更新全局用例模板映射
      setGlobalUseCaseTemplateMap(prev => {
        const updated = { ...prev }
        newUseCases.forEach(useCase => {
          updated[useCase.resourceId] = useCase.templateIds
        })
        return updated
      })
    } catch (error) {
      setUseCases([])
    }
  }
  // 监听 taskParams 变化，更新搜索参数
  useEffect(() => {
    setSearchParams(prev => ({
      ...prev,
      os: taskParams.os || '',
      templateId: taskParams.selectedTemplates || [],
      filterParams: {
        ...prev.filterParams,
        verificationModeId: taskParams.verificationModeIds || [],
      },
    }))
  }, [taskParams.os, taskParams.verificationModeIds, taskParams.selectedTemplates])

  // 监听用例选择变化，触发回调（排除全选操作，因为全选操作会直接调用回调）
  useEffect(() => {
    // 如果是全选操作，跳过这个监听器
    if (isSelectAllOperation.current) {
      return
    }

    if (onUseCaseSelectionChange && Object.keys(globalUseCaseTemplateMap).length > 0) {
      // 使用全局用例模板映射，确保包含所有已加载过的用例的模板关联信息
      // 这里只处理非全选的情况，全选操作在 selectAllUseCases 中直接处理
      onUseCaseSelectionChange(selectedUseCases, globalUseCaseTemplateMap, false)
    }
  }, [selectedUseCases, globalUseCaseTemplateMap, onUseCaseSelectionChange])

  useEffect(() => {
    // 只有在有选中的模板时才加载用例数据
    if (
      taskParams.selectedTemplates &&
      taskParams.selectedTemplates.length > 0 &&
      searchParams.templateId.length > 0
    ) {
      fetchData()
    } else {
      // 如果没有选中模板，清空用例数据
      setUseCases([])
      setTotal(0)
      setSelectedUseCases([])
      setGlobalUseCaseTemplateMap({})
    }
  }, [searchParams, taskParams.selectedTemplates])

  // 组件卸载时清理定时器
  useEffect(
    () => () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    },
    [],
  )

  // 搜索用例名称（带防抖）
  const searchUseCases = useCallback(
    (params: {
      searchType: string
      searchValue: string | undefined
      cascaderValue: any[]
      filterParams: Record<string, any>
    }) => {
      // 清除之前的定时器
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      // 设置新的定时器
      debounceTimer.current = setTimeout(() => {
        setSearchParams(prev => ({
          ...prev,
          page: 1,
          filterParams: {
            sceneId: prev.filterParams.sceneId || [],
            verificationModeId: prev.filterParams.verificationModeId || [],
            ...params.filterParams,
          },
        }))
      }, 300) // 300ms 防抖延迟
    },
    [],
  )

  // 选择用例
  const selectUseCase = useCallback((useCaseId: string, selected: boolean) => {
    if (selected) {
      setSelectedUseCases(prev => [...prev, useCaseId])
    } else {
      setSelectedUseCases(prev => prev.filter(id => id !== useCaseId))
    }
  }, [])

  // 全选用例
  const selectAllUseCases = useCallback(
    async (selected: boolean) => {
      // 设置全选操作标志
      isSelectAllOperation.current = true

      if (selected) {
        setSelectedUseCases(caseIdList)

        // 获取所有用例的完整数据来计算每个模板的已选用例数
        if (caseIdList.length > 0) {
          try {
            const params = { ...searchParams }
            delete params.page
            delete params.pageSize
            const res = await getCaseList({
              ...params,
              page: 1,
              pageSize: total > 0 ? total : 1000, // 获取所有用例
            })

            const allUseCases: UseCaseItem[] = res?.data?.list || []
            const selectedIdSet = new Set(caseIdList)
            const aggregatedTemplateMap: Record<string, string[]> = {}

            // 遍历所有用例，构建完整的用例模板映射
            allUseCases.forEach(item => {
              if (selectedIdSet.has(item.resourceId)) {
                aggregatedTemplateMap[item.resourceId] = item.templateIds || []
              }
            })

            // 更新全局用例模板映射
            setGlobalUseCaseTemplateMap(prev => ({
              ...prev,
              ...aggregatedTemplateMap,
            }))

            // 全选时传递完整的用例模板映射
            if (onUseCaseSelectionChange && Object.keys(aggregatedTemplateMap).length > 0) {
              onUseCaseSelectionChange(caseIdList, aggregatedTemplateMap, false)
            }
          } catch (error) {
            console.error('Failed to sync template selection when selecting all use cases:', error)
            // 如果获取失败，使用已有的映射
            const filteredUseCaseTemplateMap: Record<string, string[]> = {}
            caseIdList.forEach(id => {
              if (globalUseCaseTemplateMap[id]) {
                filteredUseCaseTemplateMap[id] = globalUseCaseTemplateMap[id]
              }
            })
            if (onUseCaseSelectionChange && Object.keys(filteredUseCaseTemplateMap).length > 0) {
              onUseCaseSelectionChange(caseIdList, filteredUseCaseTemplateMap, false)
            }
          }
        }
      } else {
        setSelectedUseCases([])
        // 取消全选时直接调用回调，传递空数组
        if (onUseCaseSelectionChange) {
          onUseCaseSelectionChange([], {}, false)
        }
      }

      // 重置标志
      setTimeout(() => {
        isSelectAllOperation.current = false
      }, 0)
    },
    [caseIdList, onUseCaseSelectionChange, searchParams, total, getCaseList, globalUseCaseTemplateMap],
  )

  return {
    useCases,
    loading,
    pagination: {
      current: searchParams.page,
      pageSize: searchParams.pageSize,
      total: total,
    },
    selectedUseCases,
    caseIdList,
    searchUseCases,
    selectUseCase,
    selectAllUseCases,
    handleTableChange,
    handleTableSelectChange,
    attackTypes,
    caseTagList,
    globalUseCaseTemplateMap,
  }
}
